/**
 * Iframe Height Manager
 * Automatically sends height updates to parent window for dynamic iframe sizing
 */

class IframeHeightManager {
  constructor() {
    this.isInIframe = window.self !== window.top;
    this.lastHeight = 0;
    this.resizeObserver = null;
    this.initialized = false;
    this.debounceTimer = null;
    this.updateCount = 0;
    this.maxUpdates = 10; // Limit updates per minute
  }

  init() {
    if (!this.isInIframe || this.initialized) return;

    this.initialized = true;
    this.setupResizeObserver();
    this.setupMessageListener();
    this.sendInitialHeight();

    // Send height updates periodically as fallback, but less frequently
    setInterval(() => {
      this.sendHeightUpdate();
    }, 5000); // Reduced frequency to 5 seconds

    // Reset update counter every minute
    setInterval(() => {
      this.updateCount = 0;
    }, 60000);
  }

  setupResizeObserver() {
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        this.sendHeightUpdate();
      });

      // Observe document body
      this.resizeObserver.observe(document.body);

      // Also observe the main container
      const container = document.querySelector('.app');
      if (container) {
        this.resizeObserver.observe(container);
      }
    }
  }

  setupMessageListener() {
    window.addEventListener('message', (event) => {
      if (event.data?.type === 'getHeight') {
        this.sendHeightUpdate();
      }
    });
  }

  sendInitialHeight() {
    // Wait for initial render
    setTimeout(() => {
      this.sendHeightUpdate();
    }, 100);
  }

  getDocumentHeight() {
    const body = document.body;
    const html = document.documentElement;

    return Math.max(
      body.scrollHeight,
      body.offsetHeight,
      html.clientHeight,
      html.scrollHeight,
      html.offsetHeight
    );
  }

  sendHeightUpdate() {
    if (!this.isInIframe) return;

    // Rate limiting - prevent too many updates
    if (this.updateCount >= this.maxUpdates) {
      return;
    }

    const currentHeight = this.getDocumentHeight();

    // Prevent infinite loops by setting max height limit and bigger threshold
    const maxHeight = 2500; // Maximum reasonable height
    const threshold = 50; // Bigger threshold to prevent small fluctuations

    // Only send update if height changed significantly and within reasonable bounds
    if (currentHeight <= maxHeight && Math.abs(currentHeight - this.lastHeight) > threshold) {
      // Debounce - clear existing timer
      clearTimeout(this.debounceTimer);

      this.debounceTimer = setTimeout(() => {
        this.lastHeight = currentHeight;
        this.updateCount++;

        try {
          window.parent.postMessage({
            type: 'height',
            height: currentHeight,
            source: 'leadership-test'
          }, '*');
        } catch (error) {
          console.warn('Could not send height update to parent:', error);
        }
      }, 300); // 300ms debounce delay
    }
  }

  destroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    clearTimeout(this.debounceTimer);
    this.initialized = false;
  }
}

// Create global instance
const iframeHeightManager = new IframeHeightManager();

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    iframeHeightManager.init();
  });
} else {
  iframeHeightManager.init();
}

export default iframeHeightManager;